export const ADMIN_PAGE_PERMISSIONS = Object.freeze({
  '/': 'view_dashboard',
  '/applications': 'review_applications',
  '/users': 'view_users_roles',
  '/admin-accounts': 'manage_admin_accounts',
  '/stewards': 'view_partner_network',
  '/stewards/:id': 'view_partner_network',
  '/kuds': 'view_partner_network',
  '/kuds/:id': 'view_partner_network',
  '/puskud': 'view_partner_network',
  '/rps': 'view_partner_network',
  '/rps/:id': 'view_partner_network',
  '/partners': 'view_partner_network',
  '/partners/:id': 'view_partner_network',
  '/steward-changes': 'review_steward_changes',
  '/units': 'view_units',
  '/units/:id': 'view_units',
  '/device-bindings': 'view_units',
  '/sim-cards': 'view_sim_cards',
  '/device-alerts': 'view_device_alerts',
  '/notifications': 'view_device_alerts',
  '/steward-checkins': 'review_steward_checkins',
  '/firmware': 'manage_firmware',
  '/firmware/upgrade': 'manage_firmware',
  '/firmware/upload': 'manage_firmware',
  '/firmware/list': 'manage_firmware',
  '/firmware/monitor': 'manage_firmware',
  '/finance': 'view_finance',
  '/transactions': 'view_finance',
  '/withdrawals': 'view_finance',
  '/profit-sharing-finance': 'view_finance',
  '/bank-accounts': 'view_bank_accounts',
  '/cards': 'view_physical_cards',
  '/profit-rules': 'manage_profit_rules',
  '/settings': 'manage_business_settings',
  '/settings/growth': 'manage_business_settings',
  '/audit': 'view_audit_logs',
  '/runtime-logs': 'view_runtime_logs',
  '/content': 'manage_content'
});

export const ADMIN_PERMISSION_KEYS = Object.freeze([
  'view_dashboard', 'review_applications', 'view_users_roles', 'manage_users_roles',
  'manage_admin_accounts', 'view_partner_network', 'manage_partner_bindings', 'review_steward_changes', 'view_units',
  'edit_units', 'manage_unit_pricing', 'control_units', 'view_sim_cards', 'manage_sim_cards', 'view_device_alerts', 'resolve_device_alerts',
  'manage_alert_notifications', 'review_steward_checkins', 'manage_firmware', 'view_finance',
  'approve_finance_actions', 'view_physical_cards', 'manage_physical_cards', 'write_physical_card_value',
  'view_bank_accounts', 'manage_bank_accounts', 'reveal_bank_accounts',
  'view_payouts', 'manage_payouts', 'approve_payouts',
  'manage_profit_rules', 'manage_business_settings', 'manage_permissions', 'view_audit_logs',
  'view_runtime_logs', 'manage_content'
]);

const PERMISSION_METADATA = Object.freeze({
  view_dashboard: ['Operations Monitoring', 'Page'], review_applications: ['Operations Monitoring', 'Action'],
  view_users_roles: ['Users and roles', 'Page'], manage_users_roles: ['Users and roles', 'Action', ['view_users_roles']],
  view_partner_network: ['Users and roles', 'Page', ['view_users_roles', 'view_units']], manage_partner_bindings: ['Users and roles', 'Action', ['view_partner_network', 'view_users_roles']],
  review_steward_changes: ['Device operations', 'Action', ['view_partner_network', 'view_users_roles']], view_units: ['Device operations', 'Page'],
  edit_units: ['Device operations', 'Action', ['view_units', 'view_users_roles']], manage_unit_pricing: ['Device operations', 'High-risk action', ['view_units']], control_units: ['Device operations', 'High-risk action', ['view_units']],
  view_sim_cards: ['Device operations', 'Page'], manage_sim_cards: ['Device operations', 'Action', ['view_sim_cards', 'view_units']],
  view_device_alerts: ['Device operations', 'Page'], resolve_device_alerts: ['Device operations', 'Action', ['view_device_alerts']], manage_alert_notifications: ['Device operations', 'Action', ['view_device_alerts']],
  review_steward_checkins: ['Device operations', 'Action', ['view_partner_network', 'view_units']], manage_firmware: ['Device operations', 'High-risk action'],
  view_finance: ['Finance Center', 'Page'], approve_finance_actions: ['Finance Center', 'High-risk action', ['view_finance']],
  view_bank_accounts: ['Finance Center', 'Page', ['view_finance']], manage_bank_accounts: ['Finance Center', 'Action', ['view_bank_accounts']], reveal_bank_accounts: ['Finance Center', 'Sensitive data', ['view_bank_accounts']],
  view_payouts: ['Finance Center', 'Page', ['view_finance']], manage_payouts: ['Finance Center', 'High-risk action', ['view_payouts']], approve_payouts: ['Finance Center', 'High-risk action', ['view_payouts']],
  view_physical_cards: ['Finance Center', 'Page'], manage_physical_cards: ['Finance Center', 'Action', ['view_physical_cards']], write_physical_card_value: ['Finance Center', 'High-risk action', ['manage_physical_cards']], manage_profit_rules: ['Finance Center', 'High-risk action', ['view_finance']],
  manage_business_settings: ['System management', 'Page'], manage_permissions: ['System management', 'System only', ['manage_business_settings'], false], manage_admin_accounts: ['System management', 'System only', [], false],
  view_audit_logs: ['System management', 'Page'], view_runtime_logs: ['System management', 'Page'], manage_content: ['System management', 'Action']
});

// This is intentionally a navigation tree, not another set of permissions.
// A repeated function key means that the current product intentionally shares
// one page-entry permission across more than one left-navigation page.
export const ADMIN_PERMISSION_TREE = Object.freeze([
  Object.freeze({ label: 'Operations Monitoring', pages: Object.freeze([
    Object.freeze({ label: 'Dashboard', routes: Object.freeze(['/']), functionKeys: Object.freeze(['view_dashboard']) }),
    Object.freeze({ label: 'Application requests', routes: Object.freeze(['/applications']), functionKeys: Object.freeze(['review_applications']) })
  ]) }),
  Object.freeze({ label: 'Users and roles', pages: Object.freeze([
    Object.freeze({ label: 'Users and business roles', routes: Object.freeze(['/users']), functionKeys: Object.freeze(['view_users_roles', 'manage_users_roles']) }),
    Object.freeze({ label: 'PA, RP, and steward profiles', routes: Object.freeze(['/stewards', '/kuds', '/puskud']), functionKeys: Object.freeze(['view_partner_network', 'manage_partner_bindings']) })
  ]) }),
  Object.freeze({ label: 'Device operations', pages: Object.freeze([
    Object.freeze({ label: 'Water-station management', routes: Object.freeze(['/units']), functionKeys: Object.freeze(['view_units', 'edit_units', 'manage_unit_pricing', 'control_units']) }),
    Object.freeze({ label: 'SIM card management', routes: Object.freeze(['/sim-cards']), functionKeys: Object.freeze(['view_sim_cards', 'manage_sim_cards']) }),
    Object.freeze({ label: 'Device alerts', routes: Object.freeze(['/device-alerts', '/notifications']), functionKeys: Object.freeze(['view_device_alerts', 'resolve_device_alerts', 'manage_alert_notifications']) }),
    Object.freeze({ label: 'Steward inspections', routes: Object.freeze(['/steward-checkins']), functionKeys: Object.freeze(['review_steward_checkins']) }),
    Object.freeze({ label: 'Steward change review', routes: Object.freeze(['/steward-changes']), functionKeys: Object.freeze(['review_steward_changes']) }),
    Object.freeze({ label: 'Firmware upgrades', routes: Object.freeze(['/firmware/upgrade']), functionKeys: Object.freeze(['manage_firmware']) })
  ]) }),
  Object.freeze({ label: 'Finance Center', pages: Object.freeze([
    Object.freeze({ label: 'Finance management', routes: Object.freeze(['/finance']), functionKeys: Object.freeze(['view_finance', 'approve_finance_actions']), sharedKeys: Object.freeze(['view_finance']) }),
    Object.freeze({ label: 'Transaction details', routes: Object.freeze(['/transactions']), functionKeys: Object.freeze(['view_finance']), sharedKeys: Object.freeze(['view_finance']) }),
    Object.freeze({ label: 'Income distribution', routes: Object.freeze(['/profit-sharing-finance']), functionKeys: Object.freeze(['view_finance']), sharedKeys: Object.freeze(['view_finance']) }),
    Object.freeze({ label: 'Physical water cards', routes: Object.freeze(['/cards']), functionKeys: Object.freeze(['view_physical_cards', 'manage_physical_cards', 'write_physical_card_value']) }),
    Object.freeze({ label: 'Bank accounts and payouts', routes: Object.freeze(['/bank-accounts']), functionKeys: Object.freeze(['view_bank_accounts', 'manage_bank_accounts', 'reveal_bank_accounts', 'view_payouts', 'manage_payouts', 'approve_payouts']) }),
    Object.freeze({ label: 'Income-distribution rules', routes: Object.freeze(['/profit-rules']), functionKeys: Object.freeze(['manage_profit_rules']) })
  ]) }),
  Object.freeze({ label: 'System management', pages: Object.freeze([
    Object.freeze({ label: 'System settings', routes: Object.freeze(['/settings']), functionKeys: Object.freeze(['manage_business_settings']) }),
    Object.freeze({ label: 'User growth settings', routes: Object.freeze(['/settings/growth']), functionKeys: Object.freeze(['manage_business_settings']), sharedKeys: Object.freeze(['manage_business_settings']) }),
    Object.freeze({ label: 'System audit', routes: Object.freeze(['/audit']), functionKeys: Object.freeze(['view_audit_logs']) }),
    Object.freeze({ label: 'Runtime logs', routes: Object.freeze(['/runtime-logs']), functionKeys: Object.freeze(['view_runtime_logs']) }),
    Object.freeze({ label: 'Content management', routes: Object.freeze(['/content']), functionKeys: Object.freeze(['manage_content']) })
  ]) })
]);

export const ADMIN_PERMISSION_CATALOG = Object.freeze(ADMIN_PERMISSION_KEYS.map((functionKey) => {
  const [group, kind, requires = [], delegatable = true] = PERMISSION_METADATA[functionKey];
  return Object.freeze({ functionKey, group, kind, requires: Object.freeze(requires), delegatable });
}));

const normalizePath = (pathname) => {
  const value = String(pathname || '/').split('?')[0].split('#')[0];
  if (value === '/') return value;
  return value.replace(/\/+$/, '') || '/';
};

const matchesPattern = (pathname, pattern) => {
  const pathSegments = normalizePath(pathname).split('/').filter(Boolean);
  const patternSegments = normalizePath(pattern).split('/').filter(Boolean);
  return pathSegments.length === patternSegments.length && patternSegments.every((segment, index) => (
    segment.startsWith(':') || segment === pathSegments[index]
  ));
};

export const getPagePermission = (pathname, pagePermissions = ADMIN_PAGE_PERMISSIONS) => {
  const normalized = normalizePath(pathname);
  if (pagePermissions?.[normalized]) return pagePermissions[normalized];
  const entry = Object.entries(pagePermissions || {}).find(([pattern]) => (
    pattern.includes('/:') && matchesPattern(normalized, pattern)
  ));
  return entry?.[1] || null;
};

export const hasAdminPermission = (user, functionKey) => {
  if (!user || !functionKey) return false;
  if (user.isSuperAdmin === true) return true;
  return user.permissions?.[functionKey] === true;
};

export const canAccessAdminPath = (user, pathname) => {
  const functionKey = getPagePermission(pathname, user?.pagePermissions || ADMIN_PAGE_PERMISSIONS);
  return Boolean(functionKey && hasAdminPermission(user, functionKey));
};

export const getFirstAccessibleAdminPath = (user, pagePermissions = user?.pagePermissions || ADMIN_PAGE_PERMISSIONS) => (
  Object.entries(pagePermissions || {}).find(([pathname, functionKey]) => (
    !pathname.includes(':') && hasAdminPermission(user, functionKey)
  ))?.[0] || '/'
);

export const filterMenuGroupsByPermission = (groups, user) => (groups || [])
  .map((group) => ({
    ...group,
    items: (group.items || []).filter((item) => hasAdminPermission(
      user,
      item.permission || getPagePermission(item.path, user?.pagePermissions || ADMIN_PAGE_PERMISSIONS)
    ))
  }))
  .filter((group) => group.items.length > 0);
